/**
 * Firebase Analytics for Straiv
 * Uses platform-specific native interfaces:
 * - iOS: WebKit message handlers (StraivFirebaseAnalytics)
 * - Android: Custom Capacitor plugin (StraivAnalytics)
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

// Check if we're on a native platform where Firebase is available
const isNative = Capacitor.isNativePlatform();
const platform = Capacitor.getPlatform();

console.log('[Analytics] Module loaded. isNative:', isNative, 'Platform:', platform);

// Define the interface for our custom Straiv Analytics plugin (Android)
interface StraivAnalyticsPlugin {
  logEvent(options: { name: string; params?: Record<string, any> }): Promise<void>;
  setUserProperty(options: { name: string; value: string | null }): Promise<void>;
  setUserId(options: { userId: string | null; email?: string }): Promise<void>;
}

// Defer plugin registration until first use - avoids TDZ/circular dep crash when
// Analytics is imported by RunningActive (loaded on navigation)
let _StraivAnalytics: StraivAnalyticsPlugin | null = null;
const getStraivAnalytics = (): StraivAnalyticsPlugin | null => {
  if (!_StraivAnalytics) {
    try {
      _StraivAnalytics = registerPlugin<StraivAnalyticsPlugin>('StraivAnalytics');
    } catch {
      return null;
    }
  }
  return _StraivAnalytics;
};

// Helper to get iOS analytics interface (injected by SceneDelegate)
const getIOSAnalytics = (): any => {
  return (window as any).StraivFirebaseAnalytics;
};

/**
 * Log a custom event to Firebase Analytics
 */
const logEvent = async (name: string, params?: Record<string, any>) => {
  console.log(`[Analytics] logEvent() called for: ${name}`, { isNative, platform, params });
  
  if (!isNative) {
    console.log(`[Analytics] ${name}`, params || '');
    return;
  }
  
  try {
    if (platform === 'ios') {
      // iOS: Use injected WebKit interface
      const iosAnalytics = getIOSAnalytics();
      if (iosAnalytics?.logEvent) {
        await iosAnalytics.logEvent({ name, params: params || {} });
        console.log(`[Analytics] ✅ iOS Event logged: ${name}`);
      } else {
        console.warn('[Analytics] iOS interface not available yet');
      }
    } else {
      // Android: Use Capacitor plugin
      const plugin = getStraivAnalytics();
      if (plugin) await plugin.logEvent({ name, params: params || {} });
      console.log(`[Analytics] ✅ Android Event logged: ${name}`);
    }
  } catch (error: any) {
    console.error(`[Analytics] ❌ Error calling logEvent for ${name}:`, error);
  }
};

/**
 * Set a user property
 */
const setUserProperty = async (key: string, value: string) => {
  if (!isNative) return;
  
  try {
    if (platform === 'ios') {
      const iosAnalytics = getIOSAnalytics();
      if (iosAnalytics?.setUserProperty) {
        await iosAnalytics.setUserProperty({ key, value });
        console.log(`[Analytics] ✅ iOS User property set: ${key}`);
      }
    } else {
      const plugin = getStraivAnalytics();
      if (plugin) await plugin.setUserProperty({ name: key, value });
      console.log(`[Analytics] ✅ Android User property set: ${key}`);
    }
  } catch (error) {
    console.error('[Analytics] Error setting user property:', error);
  }
};

/**
 * Set the user ID for analytics
 */
const setUserId = async (userId: string | null, email?: string | null) => {
  if (!isNative) return;
  
  try {
    if (platform === 'ios') {
      const iosAnalytics = getIOSAnalytics();
      if (iosAnalytics?.setUserId) {
        await iosAnalytics.setUserId({ userId, email: email ?? undefined });
        console.log(`[Analytics] ✅ iOS User ID set`);
      }
    } else {
      const plugin = getStraivAnalytics();
      if (plugin) await plugin.setUserId({ userId, email: email ?? undefined });
      console.log(`[Analytics] ✅ Android User ID set`);
    }
  } catch (error) {
    console.error('[Analytics] Error setting user ID:', error);
  }
};

/**
 * Log a screen view event
 */
const logScreenView = async (screenName: string, screenClass?: string) => {
  if (!isNative) {
    console.log(`[Analytics] Screen: ${screenName}`);
    return;
  }
  
  const params = {
    screen_name: screenName,
    screen_class: screenClass || screenName,
  };
  
  try {
    if (platform === 'ios') {
      const iosAnalytics = getIOSAnalytics();
      if (iosAnalytics?.logEvent) {
        await iosAnalytics.logEvent({ name: 'screen_view', params });
        console.log(`[Analytics] ✅ iOS Screen view: ${screenName}`);
      }
    } else {
      const plugin = getStraivAnalytics();
      if (plugin) await plugin.logEvent({ name: 'screen_view', params });
      console.log(`[Analytics] ✅ Android Screen view: ${screenName}`);
    }
  } catch (error) {
    console.error('[Analytics] Error logging screen view:', error);
  }
};

// ============================================
// AUTHENTICATION EVENTS
// ============================================

export const Analytics: any = {
  setUserId,
  setUserProperty,
  logScreenView,
  
  signUp: (method: 'email' | 'google' | 'apple') => 
    logEvent('sign_up', { method }),
  
  login: (method: 'email' | 'google' | 'apple') => 
    logEvent('login', { method }),
  
  logout: () => 
    logEvent('logout'),
  
  passwordResetRequested: () => 
    logEvent('password_reset_requested'),
  
  accountDeleted: () => 
    logEvent('account_deleted'),
  
  // ============================================
  // ONBOARDING EVENTS
  // ============================================
  
  onboardingStarted: () => 
    logEvent('onboarding_started'),
  
  onboardingStepCompleted: (stepNumber: number, stepName: string) => 
    logEvent('onboarding_step_completed', { step_number: stepNumber, step_name: stepName }),
  
  onboardingCompleted: (durationSeconds?: number) => 
    logEvent('onboarding_completed', { duration_seconds: durationSeconds }),
  
  onboardingAbandoned: (lastStep: number) => 
    logEvent('onboarding_abandoned', { last_step: lastStep }),
  
  // ============================================
  // NUTRITION/FOOD EVENTS
  // ============================================
  
  mealLogged: (mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack', details?: Record<string, any>) => 
    logEvent('meal_logged', { meal_type: mealType, ...details }),
  
  mealDeleted: () => 
    logEvent('meal_deleted'),
  
  foodSearched: (searchTerm: string, resultsCountOrDetails?: number | Record<string, any>) => {
    const params: Record<string, any> = { search_term: searchTerm };
    if (typeof resultsCountOrDetails === 'number') {
      params.results_count = resultsCountOrDetails;
    } else if (resultsCountOrDetails) {
      Object.assign(params, resultsCountOrDetails);
    }
    return logEvent('food_searched', params);
  },
  
  customFoodCreated: (foodName?: string) => 
    logEvent('custom_food_created', foodName ? { food_name: foodName } : {}),
  
  savedMealUsed: (mealName?: string, mealType?: string) => 
    logEvent('saved_meal_used', { ...(mealName && { meal_name: mealName }), ...(mealType && { meal_type: mealType }) }),
  
  waterLogged: (amountMlOrLiters: number) => 
    logEvent('water_logged', { amount_ml: amountMlOrLiters }),
  
  nutritionGoalProgress: (caloriesOrData: number | Record<string, any>, proteinPct?: number) => {
    if (typeof caloriesOrData === 'object') {
      return logEvent('nutrition_goal_progress', caloriesOrData);
    }
    return logEvent('nutrition_goal_progress', { calories_pct: caloriesOrData, protein_pct: proteinPct });
  },
  
  // Legacy aliases for backward compatibility
  foodLogged: (mealType: string, method: 'manual' | 'scan' | 'import') => 
    logEvent('food_logged', { meal_type: mealType, method }),
  
  foodScanStarted: (method?: 'camera' | 'upload') => 
    logEvent('food_scan_started', method ? { method } : {}),
  
  foodScanCompleted: (foodsDetectedCount: number) => 
    logEvent('food_scan_completed', { foods_detected_count: foodsDetectedCount }),
  
  foodScanFailed: (errorType: string) => 
    logEvent('food_scan_failed', { error_type: errorType }),
  
  foodRemoved: (mealType: string) => 
    logEvent('food_removed', { meal_type: mealType }),
  
  // ============================================
  // WORKOUT EVENTS
  // ============================================
  
  workoutStarted: (workoutType: 'gym' | 'running' | 'custom' | string, source?: 'program' | 'quick' | 'ai_generated') => 
    logEvent('workout_started', { workout_type: workoutType, ...(source && { source }) }),
  
  workoutCompleted: (workoutTypeOrTitle: 'gym' | 'running' | 'custom' | string, durationOrDetails?: number | Record<string, any>, exerciseCount?: number) => {
    if (typeof durationOrDetails === 'object') {
      return logEvent('workout_completed', { workout_type: workoutTypeOrTitle, ...durationOrDetails });
    }
    return logEvent('workout_completed', { workout_type: workoutTypeOrTitle, duration_minutes: durationOrDetails, exercise_count: exerciseCount });
  },
  
  workoutAbandoned: (workoutType: 'gym' | 'running' | 'custom', completionPercentage: number) => 
    logEvent('workout_abandoned', { workout_type: workoutType, completion_percentage: completionPercentage }),
  
  exerciseAdded: (exerciseName: string, category: string) => 
    logEvent('exercise_added', { exercise_name: exerciseName, category }),
  
  exerciseSetLogged: (exerciseNameOrHasWeight: string | boolean, detailsOrHasReps?: Record<string, any> | boolean, isPersonalBest?: boolean, isArnoldSet?: boolean) => {
    if (typeof exerciseNameOrHasWeight === 'string') {
      return logEvent('exercise_set_logged', { exercise_name: exerciseNameOrHasWeight, ...(typeof detailsOrHasReps === 'object' ? detailsOrHasReps : {}) });
    }
    return logEvent('exercise_set_logged', { has_weight: exerciseNameOrHasWeight, has_reps: detailsOrHasReps, ...(isPersonalBest !== undefined && { is_personal_best: isPersonalBest }), ...(isArnoldSet !== undefined && { is_arnold_set: isArnoldSet }) });
  },
  
  restTimerStarted: (durationSecondsOrExerciseName: number | string) => 
    logEvent('rest_timer_started', typeof durationSecondsOrExerciseName === 'number' ? { duration_seconds: durationSecondsOrExerciseName } : { exercise_name: durationSecondsOrExerciseName }),
  
  workoutEdited: (workoutId?: string, title?: string) => 
    logEvent('workout_edited', { ...(workoutId && { workout_id: workoutId }), ...(title && { title }) }),
  
  workoutDeleted: (workoutId?: string, title?: string) => 
    logEvent('workout_deleted', { ...(workoutId && { workout_id: workoutId }), ...(title && { title }) }),
  
  exerciseCompleted: (exerciseName: string, sets: number, totalReps: number) => 
    logEvent('exercise_completed', { exercise_name: exerciseName, sets, total_reps: totalReps }),
  
  // ============================================
  // RUNNING EVENTS - Comprehensive Tracking
  // ============================================
  
  // Pre-run screen events
  runningScreenOpened: () => 
    logEvent('running_screen_opened'),
  
  runningSettingsOpened: () => 
    logEvent('running_settings_opened'),
  
  runningSettingsChanged: (setting: string, value: string | number | boolean) => 
    logEvent('running_settings_changed', { setting, value: String(value) }),
  
  runningActivityTypeChanged: (activityType: 'outdoor' | 'treadmill') => 
    logEvent('running_activity_type_changed', { activity_type: activityType }),
  
  runningAudioIntervalChanged: (interval: string) => 
    logEvent('running_audio_interval_changed', { interval }),
  
  runningAudioVoiceChanged: (voice: 'male' | 'female') => 
    logEvent('running_audio_voice_changed', { voice }),
  
  runningWorkoutsListOpened: () => 
    logEvent('running_workouts_list_opened'),
  
  runningWorkoutSelected: (workoutTitle: string) => 
    logEvent('running_workout_selected', { workout_title: workoutTitle }),
  
  // GPS events
  runningGpsPermissionRequested: () => 
    logEvent('running_gps_permission_requested'),
  
  runningGpsPermissionGranted: () => 
    logEvent('running_gps_permission_granted'),
  
  runningGpsPermissionDenied: () => 
    logEvent('running_gps_permission_denied'),
  
  runningGpsSignalAcquired: (accuracy: number, signalStrength: string) => 
    logEvent('running_gps_signal_acquired', { accuracy_meters: accuracy, signal_strength: signalStrength }),
  
  runningGpsSignalLost: () => 
    logEvent('running_gps_signal_lost'),
  
  // Run lifecycle events
  runningWorkoutStarted: (isOutdoorOrTitle: boolean | string, details?: Record<string, any>) => 
    logEvent('running_workout_started', { 
      ...(typeof isOutdoorOrTitle === 'boolean' ? { is_outdoor: isOutdoorOrTitle } : { title: isOutdoorOrTitle }),
      ...details 
    }),
  
  runningWorkoutPaused: (elapsedSeconds: number, distanceMeters: number, pauseCount: number) => 
    logEvent('running_workout_paused', { 
      elapsed_seconds: elapsedSeconds, 
      distance_meters: Math.round(distanceMeters), 
      pause_count: pauseCount 
    }),
  
  runningWorkoutResumed: (elapsedSeconds: number, distanceMeters: number, pauseDurationSeconds: number) => 
    logEvent('running_workout_resumed', { 
      elapsed_seconds: elapsedSeconds, 
      distance_meters: Math.round(distanceMeters),
      pause_duration_seconds: pauseDurationSeconds 
    }),
  
  runningWorkoutStopped: (elapsedSeconds: number, distanceMeters: number, wasConfirmed: boolean) => 
    logEvent('running_workout_stopped', { 
      elapsed_seconds: elapsedSeconds, 
      distance_meters: Math.round(distanceMeters),
      was_confirmed: wasConfirmed 
    }),
  
  runningWorkoutCompleted: (details: {
    distanceKm: number;
    durationSeconds: number;
    avgPace: string;
    calories: number;
    pauseCount: number;
    gpsPointsCount: number;
    splitsCount: number;
  }) => 
    logEvent('running_workout_completed', { 
      distance_km: details.distanceKm,
      duration_seconds: details.durationSeconds,
      duration_minutes: Math.round(details.durationSeconds / 60),
      avg_pace: details.avgPace,
      calories: details.calories,
      pause_count: details.pauseCount,
      gps_points_count: details.gpsPointsCount,
      splits_count: details.splitsCount
    }),
  
  runningWorkoutAbandoned: (elapsedSeconds: number, distanceMeters: number, reason?: string) => 
    logEvent('running_workout_abandoned', { 
      elapsed_seconds: elapsedSeconds, 
      distance_meters: Math.round(distanceMeters),
      reason 
    }),
  
  // Milestone events during run
  runningKilometerCompleted: (km: number, splitTimeSeconds: number, totalElapsedSeconds: number) => 
    logEvent('running_kilometer_completed', { 
      kilometer: km, 
      split_time_seconds: splitTimeSeconds,
      total_elapsed_seconds: totalElapsedSeconds 
    }),
  
  runningAudioAnnouncementPlayed: (announcementType: 'start' | 'pause' | 'resume' | 'km_milestone' | 'stats' | 'finish' | 'coach_guidance') =>
    logEvent('running_audio_announcement_played', { announcement_type: announcementType }),
  
  // UI interaction events during run
  runningScreenLocked: () => 
    logEvent('running_screen_locked'),
  
  runningScreenUnlocked: () => 
    logEvent('running_screen_unlocked'),
  
  runningMapRecentered: () => 
    logEvent('running_map_recentered'),
  
  runningMapScrolled: () => 
    logEvent('running_map_scrolled'),
  
  runningStatsAnnouncementRequested: () => 
    logEvent('running_stats_announcement_requested'),
  
  // Stop confirmation dialog events
  runningStopDialogOpened: () => 
    logEvent('running_stop_dialog_opened'),
  
  runningStopDialogCancelled: () => 
    logEvent('running_stop_dialog_cancelled'),
  
  runningStopDialogConfirmed: () => 
    logEvent('running_stop_dialog_confirmed'),
  
  // Run summary events
  runningSummaryOpened: (distanceKm: number, durationSeconds: number) => 
    logEvent('running_summary_opened', { 
      distance_km: distanceKm, 
      duration_seconds: durationSeconds 
    }),
  
  runningSummaryTitleEdited: () => 
    logEvent('running_summary_title_edited'),
  
  runningSummaryFeelingSelected: (feeling: string, emoji: string) => 
    logEvent('running_summary_feeling_selected', { feeling, emoji }),
  
  runningSummaryNotesAdded: (notesLength: number) => 
    logEvent('running_summary_notes_added', { notes_length: notesLength }),
  
  runningSummarySaved: (hasNotes: boolean, hasFeeling: boolean, hasTitleEdit: boolean) => 
    logEvent('running_summary_saved', { 
      has_notes: hasNotes, 
      has_feeling: hasFeeling,
      has_title_edit: hasTitleEdit 
    }),
  
  runningSummaryDeleted: () => 
    logEvent('running_summary_deleted'),
  
  // Arnold analysis events
  runningAnalysisPromptShown: () => 
    logEvent('running_analysis_prompt_shown'),
  
  runningAnalysisRequested: (distanceKm: number, durationSeconds: number) => 
    logEvent('running_analysis_requested', { 
      distance_km: distanceKm, 
      duration_seconds: durationSeconds 
    }),
  
  runningAnalysisSkipped: () => 
    logEvent('running_analysis_skipped'),
  
  // Session persistence events
  runningSessionRestored: (elapsedSeconds: number, distanceMeters: number) => 
    logEvent('running_session_restored', { 
      elapsed_seconds: elapsedSeconds, 
      distance_meters: Math.round(distanceMeters) 
    }),
  
  runningSessionExpired: (ageMinutes: number) => 
    logEvent('running_session_expired', { age_minutes: ageMinutes }),
  
  // Tab switch during run events
  runningTabSwitchAttempted: (targetTab: string) => 
    logEvent('running_tab_switch_attempted', { target_tab: targetTab }),
  
  runningTabSwitchConfirmed: (targetTab: string) => 
    logEvent('running_tab_switch_confirmed', { target_tab: targetTab }),
  
  runningTabSwitchCancelled: () => 
    logEvent('running_tab_switch_cancelled'),
  
  // Legacy compatibility
  runningIntervalCompleted: (intervalType: string) => 
    logEvent('running_interval_completed', { interval_type: intervalType }),
  
  // ============================================
  // PROGRAM EVENTS
  // ============================================
  
  programCreated: (source: 'ai' | 'custom' | 'imported') => 
    logEvent('program_created', { source }),
  
  programStarted: (programId?: string, programTitle?: string) => 
    logEvent('program_started', { ...(programId && { program_id: programId }), ...(programTitle && { program_title: programTitle }) }),
  
  programDayCompleted: (programIdOrDayNumber: string | number, programTitleOrIsRestDay: string | boolean, dayNumber?: number) => {
    if (typeof programIdOrDayNumber === 'string') {
      return logEvent('program_day_completed', { program_id: programIdOrDayNumber, program_title: programTitleOrIsRestDay, day_number: dayNumber });
    }
    return logEvent('program_day_completed', { day_number: programIdOrDayNumber, is_rest_day: programTitleOrIsRestDay });
  },
  
  programCompleted: (programIdOrDurationWeeks: string | number, programTitle?: string) => {
    if (typeof programIdOrDurationWeeks === 'string') {
      return logEvent('program_completed', { program_id: programIdOrDurationWeeks, program_title: programTitle });
    }
    return logEvent('program_completed', { duration_weeks: programIdOrDurationWeeks });
  },
  
  programDeleted: (programId?: string, programTitle?: string) => 
    logEvent('program_deleted', { ...(programId && { program_id: programId }), ...(programTitle && { program_title: programTitle }) }),
  
  // Legacy aliases for backward compatibility
  workoutProgramCreated: (aiGenerated: boolean) => 
    logEvent('workout_program_created', { ai_generated: aiGenerated }),
  
  workoutProgramStarted: (programId: string) => 
    logEvent('workout_program_started', { program_id: programId }),
  
  // ============================================
  // AI CHAT EVENTS
  // ============================================
  
  chatOpened: () => {
    console.log('[Analytics] chatOpened() called');
    return logEvent('chat_opened');
  },
  
  chatMessageSent: (messageLength: number) => {
    console.log('[Analytics] chatMessageSent() called with length:', messageLength);
    return logEvent('chat_message_sent', { message_length: messageLength });
  },
  
  chatResponseReceived: (responseTimeMs: number) => {
    console.log('[Analytics] chatResponseReceived() called with time:', responseTimeMs);
    return logEvent('chat_response_received', { response_time_ms: responseTimeMs });
  },
  
  arnoldWorkoutSuggested: () => 
    logEvent('arnold_workout_suggested'),
  
  arnoldWorkoutAccepted: () => 
    logEvent('arnold_workout_accepted'),
  
  arnoldRecipeSuggested: () => 
    logEvent('arnold_recipe_suggested'),
  
  arnoldRecipeSaved: () => 
    logEvent('arnold_recipe_saved'),
  
  arnoldProgramGenerated: () => 
    logEvent('arnold_program_generated'),
  
  arnoldProgramSaved: () => 
    logEvent('arnold_program_saved'),
  
  arnoldAnalysisRequested: () => 
    logEvent('arnold_analysis_requested'),
  
  arnoldSuggestionDismissed: (suggestionType: string, reason?: string) => 
    logEvent('arnold_suggestion_dismissed', { suggestion_type: suggestionType, ...(reason && { reason }) }),
  
  // Legacy aliases for backward compatibility
  workoutPlanGenerated: () => 
    logEvent('workout_plan_generated'),
  
  recipeGenerated: () => 
    logEvent('recipe_generated'),
  
  aiDailyLimitReached: () => {
    console.log('[Analytics] aiDailyLimitReached() called');
    return logEvent('ai_daily_limit_reached');
  },
  
  // ============================================
  // SLEEP EVENTS
  // ============================================
  
  sleepLogged: (hoursSlept: number, qualityRating: number) => 
    logEvent('sleep_logged', { hours_slept: hoursSlept, quality_rating: qualityRating }),
  
  sleepEdited: () => 
    logEvent('sleep_edited'),
  
  sleepDeleted: () => 
    logEvent('sleep_deleted'),
  
  // ============================================
  // PROGRESS & GOALS EVENTS
  // ============================================
  
  goalCreated: (goalType: string, targetValue: number | string) => 
    logEvent('goal_created', { goal_type: goalType, target_value: targetValue }),
  
  goalUpdated: (goalType: string, exerciseName?: string) => 
    logEvent('goal_updated', { goal_type: goalType, ...(exerciseName && { exercise_name: exerciseName }) }),
  
  goalAchieved: (goalType: string, daysToAchieve?: number) => 
    logEvent('goal_achieved', { goal_type: goalType, ...(daysToAchieve !== undefined && { days_to_achieve: daysToAchieve }) }),
  
  milestoneReached: (milestoneType: string, goalType?: string) => 
    logEvent('milestone_reached', { milestone_type: milestoneType, ...(goalType && { goal_type: goalType }) }),
  
  progressViewed: (metric: string) => 
    logEvent('progress_viewed', { metric }),
  
  progressChartViewed: (chartType: string) => 
    logEvent('progress_chart_viewed', { chart_type: chartType }),
  
  // Legacy alias for backward compatibility
  goalCompleted: (goalType: string) => 
    logEvent('goal_completed', { goal_type: goalType }),
  
  // ============================================
  // APP ENGAGEMENT EVENTS
  // ============================================
  
  appOpened: (isReturningUser?: boolean, daysSinceLastOpen?: number) => 
    logEvent('app_opened', { ...(isReturningUser !== undefined && { is_returning_user: isReturningUser }), ...(daysSinceLastOpen !== undefined && { days_since_last_open: daysSinceLastOpen }) }),
  
  sessionStarted: () => 
    logEvent('session_started'),
  
  sessionEnded: (sessionDurationSeconds: number) => 
    logEvent('session_ended', { session_duration_seconds: sessionDurationSeconds }),
  
  featureDiscovered: (featureName: string) => 
    logEvent('feature_discovered', { feature_name: featureName }),
  
  tutorialStepViewed: (stepNumber: number, stepName?: string) => 
    logEvent('tutorial_step_viewed', { step_number: stepNumber, ...(stepName && { step_name: stepName }) }),
  
  tutorialCompleted: () => 
    logEvent('tutorial_completed'),
  
  tutorialSkipped: (atStep: number) => 
    logEvent('tutorial_skipped', { at_step: atStep }),
  
  tabChanged: (tabName: string) => 
    logEvent('tab_changed', { tab_name: tabName }),
  
  tabNavigation: (tabName: string) => 
    logEvent('tab_navigation', { tab_name: tabName }),
  
  // Legacy aliases for backward compatibility
  appBackgrounded: () => 
    logEvent('app_backgrounded'),
  
  appForegrounded: () => 
    logEvent('app_foregrounded'),
  
  // ============================================
  // SETTINGS & PROFILE EVENTS
  // ============================================
  
  profileUpdated: (fieldsChanged?: string[]) => 
    logEvent('profile_updated', fieldsChanged ? { fields_changed: fieldsChanged } : {}),
  
  settingsChanged: (settingName: string, newValue?: string | number | boolean) => 
    logEvent('settings_changed', { setting_name: settingName, ...(newValue !== undefined && { new_value: newValue }) }),
  
  themeChanged: (newTheme: 'light' | 'dark') => 
    logEvent('theme_changed', { new_theme: newTheme }),
  
  privacyPolicyViewed: () => 
    logEvent('privacy_policy_viewed'),
  
  termsAndConditionsViewed: () => 
    logEvent('terms_and_conditions_viewed'),
  
  supportContacted: (subject?: string) => 
    logEvent('support_contacted', subject ? { subject } : {}),
  
  // Legacy alias for backward compatibility
  settingsUpdated: (settingName: string) => 
    logEvent('settings_updated', { setting_name: settingName }),
  
  // ============================================
  // ERROR EVENTS
  // ============================================
  
  apiError: (endpoint: string, statusCode: number) => 
    logEvent('api_error', { endpoint, status_code: statusCode }),
  
  featureError: (featureName: string, errorType: string) => 
    logEvent('feature_error', { feature_name: featureName, error_type: errorType }),
  
  offlineActionAttempted: (actionType: string) => 
    logEvent('offline_action_attempted', { action_type: actionType }),
  
  // Legacy alias for backward compatibility
  errorOccurred: (errorType: string, screen?: string, details?: string) => 
    logEvent('error_occurred', { error_type: errorType, screen, details }),

  // ============================================
  // PREMIUM / PAYWALL / PURCHASES
  // ============================================

  /** Paywall dialog shown. `source` = funnel attribution (ai_run_coach, chat_limit, …). */
  paywallViewed: (source: string, details?: Record<string, any>) =>
    logEvent('paywall_viewed', { source, ...details }),

  paywallDismissed: (source: string) =>
    logEvent('paywall_dismissed', { source }),

  /** User hit a premium gate before opening paywall (feature they tried). */
  premiumGateHit: (feature: string, details?: Record<string, any>) =>
    logEvent('premium_gate_hit', { feature, ...details }),

  purchaseStarted: (productId: string, source?: string, details?: Record<string, any>) =>
    logEvent('purchase_started', { product_id: productId, ...(source && { source }), ...details }),

  purchaseCompleted: (productId: string, source?: string, details?: Record<string, any>) =>
    logEvent('purchase_completed', { product_id: productId, ...(source && { source }), ...details }),

  purchaseCancelled: (productId: string, source?: string) =>
    logEvent('purchase_cancelled', { product_id: productId, ...(source && { source }) }),

  purchaseFailed: (productId: string, errorType: string, source?: string) =>
    logEvent('purchase_failed', { product_id: productId, error_type: errorType, ...(source && { source }) }),

  purchasePending: (productId: string, source?: string) =>
    logEvent('purchase_pending', { product_id: productId, ...(source && { source }) }),

  purchaseRestored: (premium: boolean, source?: string) =>
    logEvent('purchase_restored', { premium, ...(source && { source }) }),

  restoreEmpty: (source?: string) =>
    logEvent('restore_empty', source ? { source } : {}),

  subscriptionSynced: (premium: boolean, productId?: string) =>
    logEvent('subscription_synced', { premium, ...(productId && { product_id: productId }) }),

  setPremiumStatus: (isPremium: boolean) =>
    setUserProperty('is_premium', isPremium ? 'true' : 'false'),

  // ============================================
  // AI GUIDED RUN / COACH GUIDE
  // ============================================

  runCoachSheetOpened: (details?: Record<string, any>) =>
    logEvent('run_coach_sheet_opened', details || {}),

  runCoachSheetClosed: (enabled: boolean) =>
    logEvent('run_coach_sheet_closed', { coach_enabled: enabled }),

  runPlanAsked: (coachName?: string) =>
    logEvent('run_plan_asked', coachName ? { coach_name: coachName } : {}),

  runPlanImportOpened: () =>
    logEvent('run_plan_import_opened'),

  runPlanImported: (source: 'chat' | 'saved' | 'manual' | string, details?: Record<string, any>) =>
    logEvent('run_plan_imported', { source, ...details }),

  runPlanStructured: (details: {
    phaseCount?: number;
    source?: 'ai' | 'parser' | 'already_structured' | string;
    success: boolean;
  }) =>
    logEvent('run_plan_structured', {
      success: details.success,
      ...(details.phaseCount !== undefined && { phase_count: details.phaseCount }),
      ...(details.source && { structure_source: details.source }),
    }),

  runCoachEnabled: (frequency: string, details?: Record<string, any>) =>
    logEvent('run_coach_enabled', { frequency, ...details }),

  runCoachDisabled: () =>
    logEvent('run_coach_disabled'),

  runCoachPremiumBlocked: (action: string) =>
    logEvent('run_coach_premium_blocked', { action }),

  guidedRunStarted: (details: {
    frequency: string;
    source?: string;
    phaseCount?: number;
    activityType?: string;
  }) =>
    logEvent('guided_run_started', {
      frequency: details.frequency,
      ...(details.source && { plan_source: details.source }),
      ...(details.phaseCount !== undefined && { phase_count: details.phaseCount }),
      ...(details.activityType && { activity_type: details.activityType }),
    }),

  guidedRunCompleted: (details?: Record<string, any>) =>
    logEvent('guided_run_completed', details || {}),

  liveCoachCuePlayed: (cueType?: string) =>
    logEvent('live_coach_cue_played', cueType ? { cue_type: cueType } : {}),

  runCoachFrequencyChanged: (frequency: string) =>
    logEvent('run_coach_freq_changed', { frequency }),

  // ============================================
  // SHARE WORKOUT
  // ============================================

  shareModalOpened: (workoutType: string, sourceScreen?: string) =>
    logEvent('share_modal_opened', {
      workout_type: workoutType,
      ...(sourceScreen && { source_screen: sourceScreen }),
    }),

  shareTemplateSelected: (template: string, isPremiumTemplate?: boolean) =>
    logEvent('share_template_selected', {
      template,
      ...(isPremiumTemplate !== undefined && { is_premium_template: isPremiumTemplate }),
    }),

  shareCompleted: (template: string, method: 'native' | 'web' | 'download' | string) =>
    logEvent('share_completed', { template, method }),

  shareSavedToPhotos: (template: string) =>
    logEvent('share_saved_to_photos', { template }),

  shareFailed: (template: string, errorType: string) =>
    logEvent('share_failed', { template, error_type: errorType }),

  sharePremiumTemplateBlocked: (template: string) =>
    logEvent('share_premium_blocked', { template }),

  // ============================================
  // APPLE WATCH
  // ============================================

  watchAppDetected: (installed: boolean) =>
    logEvent('watch_app_detected', { installed }),

  watchRunSessionStarted: () =>
    logEvent('watch_run_session_started'),

  watchRunSessionEnded: (endedFromWatch?: boolean) =>
    logEvent('watch_run_session_ended', {
      ...(endedFromWatch !== undefined && { ended_from_watch: endedFromWatch }),
    }),

  watchGymSessionStarted: () =>
    logEvent('watch_gym_session_started'),

  watchGymSessionEnded: () =>
    logEvent('watch_gym_session_ended'),

  watchCoachGuidePushed: (active: boolean) =>
    logEvent('watch_coach_guide_pushed', { active }),

  watchMetricsAuthoritative: () =>
    logEvent('watch_metrics_authoritative'),

  gymPlanSyncedToWatch: (exerciseCount?: number) =>
    logEvent('gym_plan_synced_to_watch', {
      ...(exerciseCount !== undefined && { exercise_count: exerciseCount }),
    }),

  // ============================================
  // LIVE ACTIVITY
  // ============================================

  liveActivityStarted: () =>
    logEvent('live_activity_started'),

  liveActivityFailed: (errorType?: string) =>
    logEvent('live_activity_failed', errorType ? { error_type: errorType } : {}),

  liveActivityEnded: () =>
    logEvent('live_activity_ended'),

  // ============================================
  // APPLE HEALTH
  // ============================================

  healthConnectTapped: () =>
    logEvent('health_connect_tapped'),

  healthConnected: () =>
    logEvent('health_connected'),

  healthAuthDenied: () =>
    logEvent('health_auth_denied'),

  healthSyncStarted: () =>
    logEvent('health_sync_started'),

  healthSyncCompleted: (success: boolean, details?: Record<string, any>) =>
    logEvent('health_sync_completed', { success, ...details }),

  healthHistoryPaywall: (metric?: string) =>
    logEvent('health_history_paywall', metric ? { metric } : {}),

  // ============================================
  // SETTINGS EXTRAS (units, coach name)
  // ============================================

  unitsChanged: (from: string, to: string) =>
    logEvent('units_changed', { from_units: from, to_units: to }),

  coachNameChanged: (newNameLength: number) =>
    logEvent('coach_name_changed', { name_length: newNameLength }),

  // ============================================
  // CHAT / AI EXTRAS
  // ============================================

  chatQuickAction: (action: string) =>
    logEvent('chat_quick_action', { action }),

  chatRunPlanImported: (details?: Record<string, any>) =>
    logEvent('chat_run_plan_imported', details || {}),

  chatWeeklyProgramRequested: (isPremium: boolean) =>
    logEvent('chat_weekly_program_req', { is_premium: isPremium }),

  chatPaywallShown: (reason: string) =>
    logEvent('chat_paywall_shown', { reason }),

  // ============================================
  // MILE / DISTANCE SPLITS (imperial-aware)
  // ============================================

  runningSplitCompleted: (splitNumber: number, unit: 'km' | 'mi', splitTimeSeconds: number) =>
    logEvent('running_split_completed', {
      split_number: splitNumber,
      unit,
      split_time_seconds: splitTimeSeconds,
    }),
};

export default Analytics;
